import { useDica } from '../ui/Dica.js';

/**
 * Mapa de calor de ocupação. Portado de `heatmap()` no protótipo.
 *
 * A escala de cor NÃO é um gradiente contínuo: são sete degraus do teal
 * (`--h0` a `--h6`, da areia ociosa ao mar fundo), e os cortes têm
 * significado operacional. Abaixo de 25% é candidato a promoção; acima
 * de 88% é lista de espera. Um gradiente suave esconde exatamente a
 * leitura que faz alguém agir.
 *
 * O denominador é a faixa de horário nobre, que é configuração da
 * arena (pendência P3) e não constante — ver 16-metricas-definicoes.md.
 */

export const DIAS_DA_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'] as const;

/** Os sete degraus, do mais vazio ao mais cheio. */
export function corDeOcupacao(percentual: number): string {
  if (percentual < 10) return 'var(--h0)';
  if (percentual < 25) return 'var(--h1)';
  if (percentual < 40) return 'var(--h2)';
  if (percentual < 55) return 'var(--h3)';
  if (percentual < 70) return 'var(--h4)';
  if (percentual < 88) return 'var(--h5)';
  return 'var(--h6)';
}

export interface FaixaDoMapa {
  /** "18", "19" — a hora, como o protótipo rotula. */
  readonly faixa: string;
  /** Um percentual por dia, na ordem de DIAS_DA_SEMANA. */
  readonly ocupacao: readonly number[];
}

export function MapaDeCalor({
  faixas,
  dias = DIAS_DA_SEMANA,
  onSelecionar,
  dicaDe,
}: {
  faixas: readonly FaixaDoMapa[];
  dias?: readonly string[];
  onSelecionar?: (dia: string, faixa: string) => void;
  /** Deixa a tela decidir o que a dica conta — receita, horas, o que for. */
  dicaDe?: (dia: string, faixa: string, percentual: number) => string;
}) {
  const dica = useDica();

  return (
    <div>
      <div className="heat" style={{ gridTemplateColumns: `52px repeat(${dias.length}, 1fr)` }}>
        <span />
        {dias.map((d) => (
          <span key={d}>{d}</span>
        ))}

        {faixas.map((linha) => (
          <Linha
            key={linha.faixa}
            linha={linha}
            dias={dias}
            aoEntrar={(dia, percentual) =>
              dicaDe
                ? {
                    titulo: `${dia} · ${linha.faixa}h`,
                    texto: dicaDe(dia, linha.faixa, percentual),
                  }
                : {
                    titulo: `${dia} · ${linha.faixa}h`,
                    texto: `Ocupação de <em>${percentual}%</em>`,
                  }
            }
            dica={dica}
            onSelecionar={onSelecionar}
          />
        ))}
      </div>

      <div className="heat-key">
        vazia
        <u />
        cheia
      </div>
    </div>
  );
}

function Linha({
  linha,
  dias,
  aoEntrar,
  dica,
  onSelecionar,
}: {
  linha: FaixaDoMapa;
  dias: readonly string[];
  aoEntrar: (dia: string, percentual: number) => { titulo: string; texto: string };
  dica: ReturnType<typeof useDica>;
  onSelecionar?: (dia: string, faixa: string) => void;
}) {
  return (
    <>
      <span>{linha.faixa}</span>
      {dias.map((dia, i) => {
        const percentual = linha.ocupacao[i] ?? 0;
        return (
          <i
            key={dia}
            style={{ background: corDeOcupacao(percentual) }}
            onMouseEnter={(e) => dica.mostrar(e, aoEntrar(dia, percentual))}
            onMouseMove={(e) => dica.mover(e)}
            onMouseLeave={() => dica.esconder()}
            onClick={() => onSelecionar?.(dia, linha.faixa)}
          />
        );
      })}
    </>
  );
}
